import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getNodesBounds, getViewportForBounds } from '@xyflow/system';
import {
  Background,
  BackgroundVariant,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Expand, Focus, Minimize2, Minus, Plus } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import { getNexusView } from '../../../api/nexus';
import { NexusDataProvider, NEXUS_VIEW_DEFAULT } from '../NexusDataContext';
import {
  nexusDefaultEdgeOptions,
  nexusInitialEdges,
  nexusInitialNodes,
  nexusNodeTypes,
} from '../nexusFlowConfig';
import '../NexusView.css';

const ZOOM_MIN = 70;
const ZOOM_MAX = 130;
const ZOOM_STEP = 10;
const DEFAULT_ZOOM = 88;

const DEFAULT_VIEWPORT = { x: 40, y: 20, zoom: DEFAULT_ZOOM / 100 };

const clampZoomPercent = (pct) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pct));

const NEXUS_LAYOUT_STORAGE_KEY = 'comply_nexus_view_layout';

/**
 * Apply viewport via Zustand store — avoids viewport prop → syncViewport → d3 interrupt.
 */
function NexusViewportSync({ viewport }) {
  const store = useStoreApi();

  useEffect(() => {
    store.setState({
      transform: [viewport.x, viewport.y, viewport.zoom],
    });
  }, [viewport.x, viewport.y, viewport.zoom, store]);

  return null;
}

function loadNexusNodesWithSavedLayout() {
  try {
    const raw = sessionStorage.getItem(NEXUS_LAYOUT_STORAGE_KEY);
    if (!raw) return nexusInitialNodes;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved)) return nexusInitialNodes;
    const byId = Object.fromEntries(saved.map((entry) => [entry.id, entry.position]));
    return nexusInitialNodes.map((node) =>
      byId[node.id] ? { ...node, position: byId[node.id] } : node
    );
  } catch {
    return nexusInitialNodes;
  }
}

function NexusFlowWorkspace({
  isFullscreen,
  onToggleFullscreen,
}) {
  const hostRef = useRef(null);
  const dragRef = useRef({ active: false, x: 0, y: 0 });
  const spacePanRef = useRef(false);
  const { getNodes } = useReactFlow();

  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);

  const initialNodes = useMemo(() => loadNexusNodesWithSavedLayout(), []);
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(nexusInitialEdges);

  const zoomPercent = clampZoomPercent(Math.round(viewport.zoom * 100));

  const setZoomPercent = useCallback((value) => {
    setViewport((prev) => {
      const currentPct = clampZoomPercent(Math.round(prev.zoom * 100));
      const nextPct = clampZoomPercent(
        typeof value === 'function' ? value(currentPct) : value,
      );
      if (Math.abs(prev.zoom - nextPct / 100) < 0.001) return prev;
      return { ...prev, zoom: nextPct / 100 };
    });
  }, []);

  const handleFitView = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const { width, height } = host.getBoundingClientRect();
    if (!width || !height) return;
    const bounds = getNodesBounds(getNodes());
    const next = getViewportForBounds(
      bounds,
      width,
      height,
      ZOOM_MIN / 100,
      ZOOM_MAX / 100,
      0.14,
    );
    setViewport(next);
  }, [getNodes]);

  const zoomIn = useCallback(() => {
    setZoomPercent((z) => z + ZOOM_STEP);
  }, [setZoomPercent]);

  const zoomOut = useCallback(() => {
    setZoomPercent((z) => z - ZOOM_STEP);
  }, [setZoomPercent]);

  const onNodeDragStop = useCallback(() => {
    try {
      const layout = nodes.map(({ id, position }) => ({ id, position }));
      sessionStorage.setItem(NEXUS_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    } catch {
      /* ignore */
    }
  }, [nodes]);

  const canStartPointerPan = useCallback((target, button) => {
    if (!(target instanceof Element)) return false;
    if (target.closest('.nexus-zoom-toolbar')) return false;
    if (target.closest('button, a, input, select, textarea')) return false;
    if (button === 1) return true;
    if (button !== 0) return false;
    if (spacePanRef.current) return true;
    return !target.closest('.nopan, .nodrag');
  }, []);

  const onPaneScroll = useCallback((event) => {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    setViewport((prev) => ({
      x: prev.x - event.deltaX,
      y: prev.y - event.deltaY,
      zoom: prev.zoom,
    }));
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'Space' && !event.repeat) spacePanRef.current = true;
    };
    const onKeyUp = (event) => {
      if (event.code === 'Space') spacePanRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const onPointerMove = (event) => {
      if (!dragRef.current.active) return;
      const dx = event.clientX - dragRef.current.x;
      const dy = event.clientY - dragRef.current.y;
      dragRef.current = {
        active: true,
        x: event.clientX,
        y: event.clientY,
      };
      setViewport((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
        zoom: prev.zoom,
      }));
    };

    const endDrag = () => {
      dragRef.current.active = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };

    const onPointerDown = (event) => {
      if (!canStartPointerPan(event.target, event.button)) return;
      event.preventDefault();
      dragRef.current = { active: true, x: event.clientX, y: event.clientY };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    };

    host.addEventListener('pointerdown', onPointerDown, { capture: true });

    return () => {
      endDrag();
      host.removeEventListener('pointerdown', onPointerDown, { capture: true });
    };
  }, [canStartPointerPan]);

  return (
    <>
      <div className="nexus-zoom-toolbar" role="toolbar" aria-label="Canvas zoom">
        <span className="nexus-zoom-label">{zoomPercent}%</span>
        <input
          type="range"
          className="nexus-zoom-slider"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={ZOOM_STEP}
          value={zoomPercent}
          onChange={(e) => setZoomPercent(Number(e.target.value))}
          aria-label="Zoom level"
        />
        <button type="button" className="nexus-zoom-btn" onClick={zoomOut} aria-label="Zoom out">
          <Minus size={14} />
        </button>
        <button type="button" className="nexus-zoom-btn" onClick={zoomIn} aria-label="Zoom in">
          <Plus size={14} />
        </button>
        <button
          type="button"
          className="nexus-zoom-btn"
          onClick={handleFitView}
          aria-label="Fit canvas to view"
          title="Fit to view"
        >
          <Focus size={14} />
        </button>
        <button
          type="button"
          className="nexus-zoom-btn"
          onClick={onToggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 size={14} /> : <Expand size={14} />}
        </button>
      </div>

      <div ref={hostRef} className="nexus-flow-host">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onPaneScroll={onPaneScroll}
          nodeTypes={nexusNodeTypes}
          defaultEdgeOptions={nexusDefaultEdgeOptions}
          defaultViewport={DEFAULT_VIEWPORT}
          minZoom={ZOOM_MIN / 100}
          maxZoom={ZOOM_MAX / 100}
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable={false}
          nodesFocusable={false}
          noPanClassName="nopan"
          noDragClassName="nodrag"
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          className="nexus-react-flow"
        >
          <NexusViewportSync viewport={viewport} />
          <Background
            variant={BackgroundVariant.Dots}
            gap={18}
            size={1.25}
            color="var(--grid-line)"
          />
          <MiniMap
            className="nexus-flow-minimap"
            pannable={false}
            zoomable={false}
            nodeColor={(node) =>
              node.type === 'readout' ? 'var(--c-neon)' : 'var(--c-teal-active)'
            }
            maskColor="color-mix(in srgb, var(--c-white) 72%, transparent)"
          />
        </ReactFlow>
      </div>
    </>
  );
}

export default function NexusView() {
  const shellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nexusData, setNexusData] = useState(NEXUS_VIEW_DEFAULT);
  const [nexusLoading, setNexusLoading] = useState(true);
  const [nexusError, setNexusError] = useState(null);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setNexusLoading(true);
    setNexusError(null);

    getNexusView()
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data;
        if (payload) setNexusData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[nexus] view fetch failed, using defaults', err);
        setNexusError(err?.message || 'Failed to load Nexus data');
      })
      .finally(() => {
        if (!cancelled) setNexusLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <PageHeader title="Nexus" />
      <div className="body nexus-view-body">
        {nexusLoading ? (
          <p className="nexus-view-status" aria-live="polite">
            Loading market intelligence…
          </p>
        ) : null}
        {nexusError && !nexusLoading ? (
          <p className="nexus-view-status nexus-view-status--warn" role="status">
            {nexusError} — showing cached layout defaults.
          </p>
        ) : null}
        <NexusDataProvider
          value={{
            data: nexusData,
            loading: nexusLoading,
            error: nexusError,
            adVideoPlaybackEnabled: nexusData?.meta?.adVideoPlaybackEnabled !== false,
          }}
        >
          <div
            ref={shellRef}
            className={`nexus-view-shell${isFullscreen ? ' nexus-view-shell--fullscreen' : ''}`}
          >
            <ReactFlowProvider>
              <NexusFlowWorkspace
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
              />
            </ReactFlowProvider>
          </div>
        </NexusDataProvider>
      </div>
    </>
  );
}
